import * as React from 'react';
import { errorForm, navigation } from '@origin-digital/reporting-client';
import { getHashOrQueryParam } from 'ts/window/WindowUtils';
import { OneTimeCodeView } from './OneTimeCodeView';
import { History } from 'history';
import * as UrlParams from 'ts/constants/UrlParams';
import * as Auth0Service from '../../../../services/auth0/Auth0Service';
import { VERIFY_ONE_TIME_CODE } from 'ts/constants/Auth0ErrorCodes';
import { CodeResendStatus } from './CodeResendStatus';
import { setFlow } from 'ts/passwordless-login/services/FlowService';
import PageConfig from '../../../../services/userflow/PageConfig';
import { FlowType } from '../../../../services/userflow/UserFlowService';

interface OneTimeCodePageState {
  emailOrPhone: string;
  oneTimeCode: string;
  codeResendStatus: CodeResendStatus;
  wrongCodeSubmitted: boolean;
  spinner: boolean;
}

interface OneTimeCodePageProps {
  history: History;
  config: PageConfig;
  flow: FlowType;
  navigateToErrorPage: () => void;
  showChangeEmailLink: boolean;
}

const RESEND_RESET_DELAY = 3000;

class OneTimeCodePage extends React.PureComponent<OneTimeCodePageProps, OneTimeCodePageState> {
  public state = {
    emailOrPhone: '',
    oneTimeCode: '',
    wrongCodeSubmitted: false,
    codeResendStatus: CodeResendStatus.Initial,
    spinner: false,
  };

  public componentDidMount() {
    this.restartFlowIfArrivedViaBackOrForward(this.props.history);
    navigation('screen', {
      currentUri: '',
      friendlyUri: `originenergy:my-account:auth:${this.props.config.analytics.pageName}`,
    });
    this.setState({ emailOrPhone: getHashOrQueryParam(UrlParams.EMAIL_OR_PHONE) });

    // set flow here to avoid any timing issues
    setFlow(this.props.flow); // required as hosted password page loses the flow
  }

  public render() {
    return (
      <OneTimeCodeView
        emailOrPhone={this.state.emailOrPhone}
        onResendClicked={this.onResendClicked}
        onFormSubmit={this.onFormSubmit}
        codeResendStatus={this.state.codeResendStatus}
        onOneTimeCodeChanged={this.onOneTimeCodeChanged}
        showWrongCodeSubmittedMessage={this.state.wrongCodeSubmitted}
        spinner={this.state.spinner}
        showChangeEmailLink={this.props.showChangeEmailLink}
      />
    );
  }

  private restartFlowIfArrivedViaBackOrForward = (history) => {
    if (history.action === 'POP') {
      // we must go to the correct path here as
      // on native, the url could be query params based or path based
      // depending on how the user arrived
      // querybased - user clicked on native link or from desktop
      // pathbased - user clicked on web link on native
      history.push(this.props.config.startRoute);
    }
  };

  private onOneTimeCodeChanged = (event) => {
    this.setState({ oneTimeCode: event.target.value });
  };

  private onFormSubmit = (event) => {
    event.preventDefault();
    this.setState({ spinner: true });

    Auth0Service.verifyOneTimeCode(
      this.state.emailOrPhone,
      this.state.oneTimeCode,
      this.props.flow,
      this.afterSubmit,
    );
  };

  private afterSubmit = (error, response) => {
    if (error) {
      const { code } = error;
      if (
        code === VERIFY_ONE_TIME_CODE.INVALID_USER_PASSWORD ||
        code === VERIFY_ONE_TIME_CODE.ACCESS_DENIED ||
        (code === VERIFY_ONE_TIME_CODE.INVALID_REQUEST && this.state.oneTimeCode === '')
      ) {
        errorForm({
          formName: this.props.config.analytics.taskName,
          errorMessage: `${this.props.config.analytics.taskName}:invalid-OTC`,
          errorCode: 'invalid-OTC',
        });
        this.setState({ spinner: false });
        this.setState({ wrongCodeSubmitted: true });
      } else {
        this.props.navigateToErrorPage();
      }
    }
  };

  private onResendClicked = async (event) => {
    this.setState({ codeResendStatus: CodeResendStatus.Sending });
    try {
      await Auth0Service.triggerPasswordlessStart(this.state.emailOrPhone);
    } catch (error) {
      errorForm({
        formName: this.props.config.analytics.taskName,
        errorMessage: `${this.props.config.analytics.taskName}:unable-to-resend-OTC`,
        errorCode: 'unable-to-resend-OTC',
      });
      this.setState({ codeResendStatus: CodeResendStatus.Failed });
      return;
    }
    this.setState({ codeResendStatus: CodeResendStatus.Sent });
    setTimeout(() => {
      this.setState({ codeResendStatus: CodeResendStatus.Initial });
    }, RESEND_RESET_DELAY);
  };
}

export { OneTimeCodePage };
